
import React, { useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle } from "react";
import moment from "moment";
import {
  Button,
  Iconography,
  IconographySizes,
  DonutChart,
  AreaMapChart,
  StackedBarChart,
  Checkbox,
} from "@hid-galaxy-ui/galaxy-react";
import "./ReportsList.scss";
import ReportEmpty from "../report-empty";
import { axiosRestInstance } from "../../../../../common/axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";
import Graph from "../report-detail/component/Graph";
import DeleteModal from "../../../../../common/components/DeleteModal";
import ErrorPopup from "../error-popup"; 
import axios from "axios";
import ChartEmpty from "../../../../../common/assets/images/Chart-empty.svg";
import { useAppSelector } from "../../../../../redux";
import { canEditCategoryByRole } from "../../../../../utils/permissions/categoryAccess";


/* ---------- Types ---------- */
type Category = {
  key: string;
  title: string;
  description: string;
  iconKey: string;
  pillBg?: string;
  pillBorder?: string;
  pillText?: string;
  count: number;
  user_view?: unknown;
  user_edit?: unknown;
};

type ChartType = "line" | "bar";
//type Timeframe = "Monthly" | "Quarterly" | "Yearly";

type ReportCard = {
  id: string;
  categoryKey: string;
  title: string;
  author: string;
  dateText: string;
  chartType: "line" | "bar";
  chart: any; // RAW chart object from API
  reportMode?: "LIVE" | "SNAPSHOT";
};

type ReportsDashboardProps = {
  categories?: Category[];
  reports?: ReportCard[];
  requestsCount?: number;
  selectedCategoryKey?: string;
  onAction?: () => void;
  reportError?: any;
  setReportError?: any;
  onReportsLoaded?: (count: number) => void;
  onSelectionChange?: (count: number) => void;
  onExitSelectionMode?: () => void;
  showReportActionButton?: boolean;
  /** Number of reports the current user is allowed to delete. */
  onDeletableReportsLoaded?: (count: number) => void;
  /** Super admin may delete SNAPSHOT reports; non–super admins cannot. */
  canDeleteSnapshotReports?: boolean;
};

type ReportErrorType =
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "SERVER_ERROR"
  | "UNKNOWN_ERROR";

type ReportError = {
  type: ReportErrorType;
  message: string;
};

export const handleAxiosError = (
  error: unknown,
  entity: string // e.g. "report", "category", "user"
): any => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    switch (status) {
      case 401:
        return {
          type: "UNAUTHORIZED",
          message: "Please log in again.",
        };

      case 403:
        return {
          type: "FORBIDDEN",
          message: `You do not have permission to view reports in this ${entity}.`,
        };

      case 404:
        return {
          type: "NOT_FOUND",
          message: `No ${entity} found.`,
        };

      case 500:
      case 502:
      case 503:
        return {
          type: "SERVER_ERROR",
          message: "Server error. Please try again later.",
        };

      default:
        return {
          type: "UNKNOWN_ERROR",
          message: "Something went wrong.",
        };
    }
  }

  return {
    type: "UNKNOWN_ERROR",
    message: "Network error. Please check your connection.",
  };
};

const getAllCategoryAPI = async () => {
  try {
    const res = await axiosRestInstance.get("/category/all");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch categories", error);
    return null;
  }
};

/* ---------- API → UI mappers ---------- */
const mapApiCategoryToUI = (cat: any): Category => ({
  key: `category-${cat.ID}`,          // string value
  title: cat.name,                    // string value
  description: cat.description,       // string value
  iconKey: "folderBookmark",          // string value
  count: cat.report_count ?? 0,      // number value
  pillBg: "#ECF3FF",                  // string value
  pillBorder: "#DBE7FB",              // string value
  pillText: "#234E9F",                // string value
  user_view: cat.user_view,
  user_edit: cat.user_edit,
});

const getReportsByCategoryAPI = async (
  userId: number,
  categoryId: string
) => {
  try {
    console.log("[API REQUEST] GET /report/list", {
      userId,
      categoryId,
    });
    const res = await axiosRestInstance.get(
      `/report/list?user_id=${userId}&category_id=${categoryId}`
    );
    console.log("[API RESPONSE] /report/list →", res.data);
    return res.data;
  } catch (error) {
    console.error("Failed to fetch reports", error);
    return error;
  }
};

const deleteReportAPI = async (reportId: string | number, userId: number) => {
  try {
    const res = await axiosRestInstance.delete("/report/delete", {
      data: {
        report_id: reportId,
        user: userId,
      },
    });
    return res.data;
  } catch (err) {
    console.error("Failed to delete report:", err);
    return { success: false, message: "Failed to delete report" };
  }
};

const mapApiReportToCard = (item: any): ReportCard => ({
  id: item.id.toString(),
  categoryKey: item.definition?.category_id
    ? `category-${item.definition.category_id}`
    : "", // <-- empty string if missing
  title: item.title,
  author: "",
  dateText: item.last_generated
    ? new Date(item.last_generated).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    : "Not generated",
  chartType: item.chart?.type?.toLowerCase() === "bar" ? "bar" : "line",
  chart: item.chart,
  reportMode: item.report_mode === "SNAPSHOT" ? "SNAPSHOT" : "LIVE",
});

const getUserByIdAPI = async (userId: number) => {
  try {
    const res = await axiosRestInstance.get(`/user/getUser/?ID=${userId}`);
    return res.data;
  } catch (error) {
    console.error("Failed to fetch user", error);
    return null;
  }
};

type BuiltChartData =
  | {
    chartType: "line";
    Data: { key: string; value: number }[];
    AreaConfig: {
      dataKey: string;
      label: string;
      fill: string;
    }[];
    xAxisLabel?: string;
      yAxisLabel?: string;
  }
  | {
    chartType: "bar";
    Data: Record<string, any>[];
    BarConfig: {
      dataKey: string;
      label: string;
      fill: string;
    }[];
    xAxisLabel?: string;
      yAxisLabel?: string;
  };

  const normalizeChart = (chart: any): {
  type: "line" | "bar";
  categories: string[];
  series: { name: string; values: number[] }[];
} => {
  if (!chart || !Array.isArray(chart.xAxis) || !Array.isArray(chart.series)) {
    return { type: "line", categories: [], series: [] };
  }

  return {
    type: chart.type?.toLowerCase() === "bar" ? "bar" : "line",
    categories: chart.xAxis.map((x: any) =>
      x?.label !== undefined ? String(x.label) : ""
    ),
    series: chart.series.map((s: any) => ({
      name: s.name ?? "Value",
      values: Array.isArray(s.data) ? s.data : [],
    })),
  };
};

// -------------------- HELPERS --------------------
const buildChartData = (chart: any): BuiltChartData | null => {
  const normalized = normalizeChart(chart);

  if (!normalized.categories.length || !normalized.series.length) {
    return null;
  }

  // ---------- LINE ----------
  if (normalized.type === "line") {
    const Data = normalized.categories.map((label, index) => {
      const row: any = { key: label };
      normalized.series.forEach((s) => {
        row[s.name] = s.values[index] ?? 0;
      });
      return row;
    });

    const AreaConfig = normalized.series.map((s) => ({
      dataKey: s.name,
      label: s.name,
      fill: "#B3DDCC",
    }));

    return {
      chartType: "line",
      Data,
      AreaConfig,
      yAxisLabel: normalized.series.map(s => s.name).join(", "),
    };
  }

  // ---------- BAR ----------
  if (normalized.type === "bar") {
    const colors = [
      "#00539B", "#F8971D", "#60116A",
      "#00AEEF", "#FFD200", "#B0CBEA",
      "#6A1B9A", "#2E7D32",
    ];

    const Data = normalized.categories.map((label, index) => {
      const row: any = { name: label };
      normalized.series.forEach((s) => {
        row[s.name] = s.values[index] ?? 0;
      });
      return row;
    });

    const BarConfig = normalized.series.map((s, index) => ({
      dataKey: s.name,
      label: s.name,
      fill: colors[index % colors.length],
    }));

    return {
      chartType: "bar",
      Data,
      BarConfig,
      yAxisLabel: normalized.series.map(s => s.name).join(", "),
    };
  }

  return null;
};


// Right-side chevron (same for both Chart & Time dropdowns)
const DROPDOWN_CHEVRON_ICON = "angleDown";

// Edit chip icon (left side)
const EDIT_ICON = "pen"; //

/* ---------- Horizontal scroll ---------- */
function useHorizontalScrollState() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  return { ref, atStart, atEnd };
}

/* ---------- Custom DropdownChip (ul/li menu with icons) ---------- */
type DropdownOption<T extends string> = { label: string; value: T };

type DropdownChipProps<T extends string> = {
  id: string;
  value: T;
  options: DropdownOption<T>[];
  onChange: (next: T) => void;
  dropdownIcon?: string; // right-side chevron (same for chart & time)
  renderValueIcon?: (value: T) => React.ReactNode;   // left icon in chip (diff for chart/time)
  renderOptionIcon?: (value: T) => React.ReactNode;  // left icon in menu items (optional)
};

const DropdownChip = <T extends string>({
  id,
  value,
  options,
  onChange,
  dropdownIcon,
  renderValueIcon,
  renderOptionIcon,
}: DropdownChipProps<T>) => {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number>(() => {
    const idx = options.findIndex((o) => o.value === value);
    return Math.max(0, idx);
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (open && menuRef.current) {
      const li = menuRef.current.querySelectorAll<HTMLLIElement>("li[role='option']")[focusIndex];
      li?.focus();
    }
  }, [open, focusIndex]);

  const currentLabel = options.find((o) => o.value === value)?.label ?? "";

  const onToggle = () => setOpen((prev) => !prev);

  const onKeyDownChip = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      setFocusIndex(options.findIndex((o) => o.value === value));
    }
  };

  const onKeyDownItem = (e: React.KeyboardEvent<HTMLLIElement>, idx: number) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIndex((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = options[idx];
      onChange(opt.value);
      setOpen(false);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="gdl-chip gdl-chip--menu" ref={rootRef}>
      <button
        id={id}
        className="gdl-chip__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        onClick={onToggle}
        onKeyDown={onKeyDownChip}
      >
        {/* LEFT: value-specific icon (different for Chart vs Time) */}
        {renderValueIcon && (
          <span className="gdl-chip__leading-icon" aria-hidden="true">
            {renderValueIcon(value)}
          </span>
        )}

        {/* LABEL: selected value */}
        <span className="gdl-chip__value">{currentLabel}</span>

        {/* RIGHT: same chevron icon for both dropdowns */}
        <span className="gdl-chip__chevron" aria-hidden="true">
          <Iconography icon={dropdownIcon ?? DROPDOWN_CHEVRON_ICON} size={IconographySizes.Small} />
        </span>
      </button>

      {open && (
        <ul
          id={`${id}-menu`}
          role="listbox"
          className="gdl-menu"
          ref={menuRef}
          aria-labelledby={id}
        >
          {options.map((opt, idx) => (
            <li
              key={opt.value}
              role="option"
              tabIndex={-1}
              aria-selected={opt.value === value}
              className={`gdl-menu__item ${opt.value === value ? "is-selected" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              onKeyDown={(e) => onKeyDownItem(e, idx)}
            >
              <span className="gdl-menu__text">{opt.label}</span>

              {/* Selected checkmark on right */}
              {opt.value === value && (
                <Iconography icon="circleCheck" size={IconographySizes.Small} aria-hidden="true" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ---------- ActionChip (styled like dropdown chip, with left edit icon) ---------- */
type ActionChipProps = {
  id?: string;
  label: string;
  onClick: () => void;
  ariaLabel?: string;
  icon?: string;
};
const ActionChip: React.FC<ActionChipProps> = ({ id, label, ariaLabel, onClick, icon = EDIT_ICON }) => {
  return (
    <div className="gdl-chip gdl-chip--menu gdl-chip--action">
      <button
        id={id}
        className="gdl-chip__button"
        onClick={onClick}
        aria-label={ariaLabel ?? label}
      >
        {/* LEFT: edit icon */}
        <span className="gdl-chip__leading-icon" aria-hidden="true">
          <Iconography icon={icon} size={IconographySizes.Small} />
        </span>

        {/* LABEL */}
        <span className="gdl-chip__value">{label}</span>

        {/* No right chevron for action */}
      </button>
    </div>
  );
};

/* ---------- Card View ---------- */
const ReportCardView: React.FC<{
  card: ReportCard;
  selectionMode: boolean;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  fallbackCategoryKey?: string;
  canSelectForDelete?: boolean;
}> = ({
  card,
  selectionMode,
  selected,
  onSelect,
  fallbackCategoryKey,
  canSelectForDelete = false,
}) => {

  const navigate = useNavigate();

  const chartData = useMemo(
    () => buildChartData(card.chart),
    [card.chart]
  );

  // Use card.categoryKey first, fallback if missing
  const categoryId = card.categoryKey?.replace("category-", "") || fallbackCategoryKey;
  
  const onEdit = () => {
    if (!categoryId) {
      console.error("[NAVIGATE] categoryId is missing, cannot navigate", card);
      return;
    }

    navigate(
      `/hid-admin/dashboard/reports-dashboard/category/${categoryId}/report/${card.id}`,
      {
        state: { category: { key: `category-${categoryId}`, title: card.title } }
      }
    );

  };

  return (
    <article className="gdl-card">
      <header className="gdl-card__header">
        <div>
          <h3 className="gdl-card__title">{card.title}</h3>
          <div className="gdl-card__meta">
            <span>By {card.author}</span> <span className="gdl-card__dot"></span> <span>{moment(card.dateText).format("DD MMM YYYY")}</span>
          </div>
        </div>
        {selectionMode && canSelectForDelete && (
          <Checkbox
            value={card.id} // required
            checked={selected}
            onChange={(e, eventResult) => onSelect(card.id, eventResult.checked)}
            className="gdl-card__checkbox"
            size="small"
            color="primary"
          />
        )}
        {!selectionMode && (
          <button
            className="gdl-card__arrow"
            onClick={() => {
              if (!categoryId) {
                console.error("[NAVIGATE] categoryId is missing, cannot navigate", card);
                return;
              }

              navigate(
                `/hid-admin/dashboard/reports-dashboard/category/${categoryId}/report/${card.id}`,
                {
                  state: { category: { key: `category-${categoryId}`, title: card.title } }
                }
              );
            }}
          >
            <Iconography icon="arrowRight" size={IconographySizes.Small} />
          </button>
        )}
      </header>

      <div className="gdl-card__chart">
        {chartData ? (
          <Graph data={chartData} chartType={chartData.chartType} truncateXAxisLabels={true} />
        ) : (
          <div className="gdl-card__empty-chart">
            <img
              src={ChartEmpty} // <-- path to your empty image
              alt="No data available"
              className="gdl-card__empty-image"
            />
            <p className="gdl-card__empty-text">No chart available for this report</p>
          </div>
        )}
      </div>

      {/* <footer className="gdl-card__footer">
        <ActionChip label="Edit" onClick={onEdit} />
      </footer> */}
    </article>
  );
};

export type ReportsListHandle = {
  openDeleteModal: () => void;
  enterSelectionMode: () => void;
  exitSelectionMode: () => void;
  selectedCount: number;
};

/* ---------- Page Component ---------- */
const ReportsList = forwardRef<ReportsListHandle, ReportsDashboardProps>(
  (
    {
      categories,
      reports,
      requestsCount = 4,
      onAction,
      reportError,
      setReportError,
      onReportsLoaded,
      onSelectionChange,
      onExitSelectionMode,
      showReportActionButton = true,
      onDeletableReportsLoaded,
      canDeleteSnapshotReports = false,
    },
    ref
  ) => {
  const [cats, setCats] = useState<Category[]>(categories ?? []);
  const [cards, setCards] = useState<ReportCard[]>(reports ?? []);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const location = useLocation();
  const { categoryKey: categoryKeyParamRaw } = useParams<{ categoryKey: string }>();
  const [userCache, setUserCache] = useState<Record<number, string>>({});
  const [loadingReports, setLoadingReports] = useState(false);
  const [hasFetchedReports, setHasFetchedReports] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false); // control modal visibility
  const [deleteButtonLoading, setDeleteButtonLoading] = useState(false); // show loading while deleting
  //const [reportToDelete, setReportToDelete] = useState<ReportCard | null>(null); // report to delete
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  
  const [popup, setPopup] = useState({
    open: false,
    message: "",
    type: "error" as "success" | "error",
  });

  const canDeleteReport = (card: ReportCard) => {
    // Protected system report — never selectable for delete (all roles)
    if (Number(card.id) === 7) {
      return false;
    }
    if (card.reportMode === "SNAPSHOT") {
      return canDeleteSnapshotReports;
    }
    return true;
  };

  const deletableReportsCount = useMemo(
    () => cards.filter((card) => canDeleteReport(card)).length,
    [cards, canDeleteSnapshotReports]
  );

  // Notify parent whenever the list of reports changes
  useEffect(() => {
    if (cards && onReportsLoaded) {
      onReportsLoaded(cards.length);
    }
  }, [cards, onReportsLoaded]);

  useEffect(() => {
    onDeletableReportsLoaded?.(deletableReportsCount);
  }, [deletableReportsCount, onDeletableReportsLoaded]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedReportIds.size);
    }
  }, [selectedReportIds, onSelectionChange]);

  const openDeleteModal = () => {
    if (selectedReportIds.size === 0) return;

    setDeleteOpen(true); // show the DeleteModal
  };

  useImperativeHandle(ref, () => ({
    openDeleteModal,
    enterSelectionMode: () => {
      if (deletableReportsCount === 0) return;
      setSelectionMode(true);
    },
    exitSelectionMode: () => {
      setSelectionMode(false);
      setSelectedReportIds(new Set()); 
    },
    selectedCount: selectedReportIds.size,
  }));

  const handleSelectReport = (id: string, checked: boolean) => {
    const card = cards.find((item) => item.id === id);
    if (!card || !canDeleteReport(card)) return;

    setSelectedReportIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const resetSelectionState = () => {
    setSelectionMode(false);
    setSelectedReportIds(new Set());
    onExitSelectionMode?.();
  };

  // Normalize URL param to match API-generated keys
  const selectedCategoryKey = categoryKeyParamRaw
    ? `category-${categoryKeyParamRaw}`  // don't double-replace
    : location.state?.selectedCategoryKey;

  const navigate = useNavigate();

  const handleBulkDeleteReports = async () => {
    if (selectedReportIds.size === 0) return;

    const idsToDelete = Array.from(selectedReportIds).filter((id) => {
      const card = cards.find((item) => item.id === id);
      return card ? canDeleteReport(card) : false;
    });
    if (idsToDelete.length === 0) return;

    setDeleteButtonLoading(true);
    const userId = loggedUserDetail.ID;

    try {
      await Promise.all(
        idsToDelete.map((reportId) =>
          deleteReportAPI(reportId, userId)
        )
      );

      // Remove deleted cards
      setCards((prev) =>
        prev.filter((r) => !idsToDelete.includes(r.id))
      );

      const deletedCount = idsToDelete.length;

      // Update category count
      setCats((prev) =>
        prev.map((cat) =>
          cat.key === selectedCategory?.key
            ? { ...cat, count: Math.max(0, cat.count - deletedCount) }
            : cat
        )
      );

      setSelectedCategory((prev) =>
        prev ? { ...prev, count: Math.max(0, prev.count - deletedCount) } : prev
      );

      setPopup({
        open: true,
        message: `${deletedCount} report(s) deleted successfully.`,
        type: "success",
      });

      setDeleteOpen(false);
      setSelectionMode(false);
      setSelectedReportIds(new Set());
      onExitSelectionMode?.();
    } catch {
      setPopup({
        open: true,
        message: "Failed to delete selected reports.",
        type: "error",
      });
    } finally {
      setDeleteButtonLoading(false);
    }
  };

  // initial load
  // Load categories on mount and set initial selected category
  useEffect(() => {
    const loadCategories = async () => {
      const catResult = await getAllCategoryAPI();
      if (!catResult?.success) return;

      const mappedCategories: Category[] = catResult.data.map(mapApiCategoryToUI);
      setCats(mappedCategories);

      if (categoryKeyParamRaw) {
        const selected = mappedCategories.find(
          (c) => c.key === `category-${categoryKeyParamRaw}`
        );
        setSelectedCategory(selected ?? null);
      }
    };

    loadCategories();
  }, [categoryKeyParamRaw]); // ✅ IMPORTANT

  // Sync selectedCategory with URL param after categories load
  useEffect(() => {
    if (!categoryKeyParamRaw || cats.length === 0) return;

    const selected = cats.find(c => c.key === `category-${categoryKeyParamRaw}`);
    setSelectedCategory(selected ?? null);
  }, [cats, categoryKeyParamRaw]);

  // Fetch reports whenever the selected category changes
  useEffect(() => {
    if (!selectedCategory) {
      setCards([]);
      setHasFetchedReports(false);
      return;
    }

    const fetchReports = async () => {
      setLoadingReports(true);
      setHasFetchedReports(false);

      try {
        const userId = loggedUserDetail.ID;
        const categoryId = selectedCategory.key.replace("category-", "");

        const reportResult = await getReportsByCategoryAPI(userId, categoryId);

        if (!reportResult?.success || !Array.isArray(reportResult.data)) {
          setCards([]);
          setReportError(handleAxiosError(reportResult, "category"));
          return;
        }

        const reports = reportResult.data;
        setReportError(null);
        const userIds: number[] = Array.from(
          new Set<number>(
            reports.map((r: any) => Number(r.created_by))
          )
        );

        const newUserCache = { ...userCache };

        await Promise.all(
          userIds.map(async (id) => {
            if (!newUserCache[id]) {
              const userRes = await getUserByIdAPI(id);
              if (userRes?.success) {
                newUserCache[id] = userRes.data.first_name;
              }
            }
          })
        );

        setUserCache(newUserCache);

        setCards(
          reports.map((r: any) => ({
            ...mapApiReportToCard(r),
            author: newUserCache[r.created_by] || `User ${r.created_by}`,
          }))
        );
      } catch (err) {
        console.error("Failed to fetch reports:", err);
        setCards([]);
      } finally {
        setLoadingReports(false);
        setHasFetchedReports(true); // ✅ IMPORTANT
      }
    };

    fetchReports();
  }, [selectedCategory]);

  const filteredCards = cards;

  /* Horizontal slider state for categories */
  const { ref: catsScrollRef, atStart, atEnd } = useHorizontalScrollState();

  const scrollByAmount = (px: number) => {
    const el = catsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: px, behavior: "smooth" });
  };

  /* ---------- Handle category selection ---------- */
  const handleCategorySelect = (cat: Category) => {

    // RESET selection mode when switching category
    resetSelectionState();

    // Update URL
    //navigate(`category/${cat.key.replace("category-", "")}`, { replace: false });
    navigate(
      `/hid-admin/dashboard/reports-dashboard/category/${cat.key.replace("category-", "")}`,
      { replace: false }
    );
    // Update selected category (triggers report fetch)
    setSelectedCategory(cat);

    // Optional: update page title or breadcrumbs
    //document.title = `${cat.title} Reports`;
    const breadcrumbEl = document.querySelector(".gdl-breadcrumb-current");
    if (breadcrumbEl) breadcrumbEl.textContent = cat.title;
  };

  const ReportsLoader: React.FC = () => (
    <div className="gdl-page-loader">
      <Loader variant={LoaderSizes.large} />
    </div>
  );

  const categoryLevelEdit = selectedCategory
    ? canEditCategoryByRole(loggedUserDetail?.role, selectedCategory.user_edit)
    : false;
  const showReportActions = showReportActionButton && categoryLevelEdit;

  return (
    <>

    {/* NEW ERROR POPUP */}
    <ErrorPopup
      open={popup.open}
      message={popup.message}
      type={popup.type}
      onClose={() => setPopup({ open: false, message: "", type: "error" })}
    />

    {deleteOpen && (
      <DeleteModal
        setDeleteOpen={setDeleteOpen}
        onDeleteButtonClick={handleBulkDeleteReports}
        deleteButtonLoading={deleteButtonLoading}
        message={`This will delete ${selectedReportIds.size} report(s) permanently.`}
      />
    )}

    <div className="gdl-reports">
      <h2 className="gdl-section-title">Categories</h2>

      {/* Horizontal slider for categories */}
      <section className="gdl-categories-row" aria-label="Categories">
        {/* Left arrow */}
        <Button
          appearance="outlined"
          disabled={atStart}
          className="gdl-cat-scroll-btn gdl-cat-scroll-btn--prev"
          onClick={() => scrollByAmount(-320)}
          aria-label="Scroll categories left"
          label={
            <Iconography icon="angleLeft" size={IconographySizes.Small} />
          }
        />

        {/* Category pills */}
        <div ref={catsScrollRef} className="gdl-cat-scroll" role="listbox">
          {cats.map((cat) => (
            <button
              key={cat.key}
              className={`gdl-cat-pill ${selectedCategory?.key === cat.key ? "gdl-cat-pill--selected" : ""
                }`}
              onClick={() => handleCategorySelect(cat)}
              aria-pressed={selectedCategory?.key === cat.key}
              style={
                {
                  "--pill-bg": cat.pillBg,
                  "--pill-border": cat.pillBorder,
                  "--pill-text": cat.pillText,
                } as React.CSSProperties
              }
            >
              <div className="gdl-cat__content">
                <span className="gdl-cat-pill__title">{cat.title}</span>
                <span className="gdl-cat-pill__count">
                  {cat.count} {cat.count === 1 ? "Report" : "Reports"}
                </span>
              </div>

              {selectedCategory?.key === cat.key ? (
                <Iconography
                  icon="circleCheck"
                  size={IconographySizes.Small}
                  className="gdl-cat-pill__icon"
                />
              ) : (
                <div className="gdl-cat-pill__radio" />
              )}
            </button>
          ))}
        </div>


        {/* Right arrow */}
        <Button
          appearance="outlined"
          disabled={atEnd}
          className="gdl-cat-scroll-btn gdl-cat-scroll-btn--next"
          onClick={() => scrollByAmount(320)}
          aria-label="Scroll categories right"
          label={
            <Iconography icon="angleRight" size={IconographySizes.Small} />
          }
        />
      </section>

      {/* Once reports are there to show we will uncomment this block of code */}
      {loadingReports ? (
        <ReportsLoader />
      ) : hasFetchedReports && filteredCards.length > 0 ? (
        <section className="gdl-grid" aria-label="Reports Grid">
          {filteredCards.map((card) => (
            <ReportCardView
              key={card.id}
              card={card}
              selectionMode={selectionMode}
              selected={selectedReportIds.has(card.id)}
              onSelect={handleSelectReport}
              fallbackCategoryKey={selectedCategory?.key.replace("category-", "")}
              canSelectForDelete={canDeleteReport(card)}
            />
          ))}
        </section>
      ) : hasFetchedReports ? (
        <ReportEmpty
          type="report"
          onAction={onAction}
          reportError={reportError}
          showButton={showReportActions}
        />
      ) : null}
    </div>
    </>
  );
});

export default ReportsList;
